// Configuration progress bar — a thin strip under the header, above the tabs.
//
// Measures the SAME thing Save & Submit does: how many of the fields that are
// required RIGHT NOW have been answered (see getCompletionProgress, which shares
// one traversal with findUnansweredVisible). So 100% means "submitting will not
// flag anything", and the bar can never disagree with the button.
//
// The denominator moves as sections reveal and hide. That's deliberate: it
// reflects the work actually left in front of the user, rather than a fixed field
// count that no single configuration ever reaches.

import { useMemo } from "react";
import type { CpqData } from "../data/loader";
import { useAnswers } from "../state/AnswersContext";
import { useRequiredFields } from "../state/RequiredFields";
import { getCompletionProgress, getVisibleQuestionProgress } from "../util/validation";
import { POINTS_PER_CONFIG } from "../util/pointsClient";

type Props = {
  data: CpqData;
  /** Open the field's tab, scroll to it and put the cursor in it. */
  onJump?: (tabId: string, code: string) => void;
};

export default function ProgressBar({ data, onJump }: Props) {
  const { answers, configStatus } = useAnswers();
  // the same traversal the field marks read (state/RequiredFields), not a second one
  const { fields, next } = useRequiredFields();
  const { percent, answered, total, remaining } = useMemo(
    () => getCompletionProgress(data, answers, fields),
    [data, answers, fields]
  );
  // The tab pills count every question the user can see on the tab, not only the required
  // ones (Kevin, 2026-10-19: "13/14 when there is more than 14 questions on the firetube tab").
  // The bar and "Ready to submit" keep the required-field meaning above.
  const byTab = useMemo(() => getVisibleQuestionProgress(data, answers), [data, answers]);

  const complete = total > 0 && remaining === 0;
  // Only a genuinely NEW config earns points (Kevin, 2026-08-12), so only a new config is
  // promised any. Editing updates a part that already exists, and "existing" builds nothing
  // at all - claiming +100 in either case was the bar advertising a reward that
  // scoreConfiguration would then refuse.
  const earnsPoints = configStatus === "new";

  return (
    <div
      className="cpq-progress"
      data-complete={complete ? "yes" : "no"}
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Configuration completeness"
      title={
        total === 0
          ? "Answer the Customer Requirements to begin"
          : `${answered} of ${total} required fields answered`
      }
    >
      <div className="cpq-progress-track">
        <div className="cpq-progress-fill" style={{ width: `${percent}%` }} />
      </div>

      {/* Per-tab running totals: visible questions answered / visible questions. Both tabs
          are listed from the start — a tab showing 0/0 has simply revealed nothing yet, which
          is far clearer than leaving it out and having Firetube's work appear from nowhere. */}
      <div className="cpq-progress-tabs">
        {byTab.map((t) => (
          <span
            key={t.tabId}
            className="cpq-progress-tab"
            data-done={t.total > 0 && t.answered === t.total ? "yes" : "no"}
            title={`${t.tab}: ${t.answered} of ${t.total} questions answered`}
          >
            {t.tab} <strong>{t.answered}/{t.total}</strong>
          </span>
        ))}
      </div>

      <div className="cpq-progress-label">
        {complete ? (
          <>
            <strong>Ready to submit</strong> — {total} fields
            {earnsPoints && (
              <>
                {" "}·{" "}
                <span className="cpq-progress-points">+{POINTS_PER_CONFIG} pts</span>
              </>
            )}
          </>
        ) : total === 0 ? (
          <>Start with Customer Requirements</>
        ) : (
          <>
            <strong>{percent}%</strong> · {remaining} to go
            {/* "3 to go" said how much, never which: name the next one and go to it. */}
            {next && onJump && (
              <button
                type="button"
                className="cpq-progress-next"
                onClick={() => onJump(next.tabId, next.code)}
                title={`Go to ${next.label} (${next.tab})`}
              >
                Next: {next.label}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
